import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { getPaymentSettings, updatePaymentSettings } from '../services/paymentService';
import { PaymentSettings } from '../types/Payment';
import styles from '../Payments.module.css';

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });

const providerLabel = (code: string, t: (key: string) => string): string =>
  code.toLowerCase() === 'pasha' ? t('providerPasha') : code;

export const PaymentSettingsPage: React.FC = () => {
  const { t } = useTranslation(['payments', 'common']);
  const { hasPermission } = useAuth();
  const canSave = hasPermission(PermissionScopes.paymentsSettings);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [settings, setSettings] = React.useState<PaymentSettings | null>(null);
  const [enabled, setEnabled] = React.useState(false);
  const [providerCode, setProviderCode] = React.useState('pasha');
  const [currency, setCurrency] = React.useState('AZN');
  const [language, setLanguage] = React.useState('az');
  const [environment, setEnvironment] = React.useState('live');
  const [returnPath, setReturnPath] = React.useState('');
  const [pkcs12Password, setPkcs12Password] = React.useState('');
  const [pkcs12File, setPkcs12File] = React.useState<File | null>(null);
  const [caCertificatePem, setCaCertificatePem] = React.useState('');
  const [merchantHandlerUrl, setMerchantHandlerUrl] = React.useState('');
  const [clientHandlerUrl, setClientHandlerUrl] = React.useState('');
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPaymentSettings();
        setSettings(data);
        setEnabled(data.enabled);
        setProviderCode(data.providerCode || data.availableProviders[0] || 'pasha');
        setCurrency(data.currency || 'AZN');
        setLanguage(data.language || 'az');
        setEnvironment(data.environment || 'live');
        setReturnPath(data.returnPath ?? '');
      } catch (err) {
        setError(err instanceof Error ? err.message : t('failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [t]);

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    const needsCertificate = !settings?.hasCredentials;
    if ((needsCertificate || pkcs12File) && (!pkcs12File || !pkcs12Password.trim())) {
      setError(t('certificateRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const pkcs12Base64 = pkcs12File ? await readFileAsBase64(pkcs12File) : undefined;
      const updated = await updatePaymentSettings({
        enabled,
        providerCode,
        currency,
        language,
        environment,
        returnPath: returnPath.trim() || undefined,
        pkcs12Base64,
        pkcs12Password: pkcs12Password.trim() || undefined,
        caCertificatePem: caCertificatePem.trim() || undefined,
        merchantHandlerUrl: merchantHandlerUrl.trim() || undefined,
        clientHandlerUrl: clientHandlerUrl.trim() || undefined,
      });
      setSettings(updated);
      setPkcs12File(null);
      setPkcs12Password('');
      setSuccess(t('settingsSaved'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner text={t('loading')} />;
  }

  return (
    <form className={styles.sectionCard} onSubmit={onSave}>
      <p className={styles.sectionIntro}>{t('pciNotice')}</p>
      {error ? <div className={styles.error}>{error}</div> : null}
      {success ? <div className={styles.success}>{success}</div> : null}

      <div className={styles.formGrid}>
        <label className={`${styles.field} ${styles.fieldWide} ${styles.checkboxRow}`}>
          <input type="checkbox" checked={enabled} disabled={!canSave} onChange={event => setEnabled(event.target.checked)} />
          <span>
            {t('enabled')}
            <span className={styles.hint}> — {t('enabledHint')}</span>
          </span>
        </label>

        <div className={styles.field}>
          <label className={styles.label}>{t('providerCode')}</label>
          <select value={providerCode} disabled={!canSave} onChange={event => setProviderCode(event.target.value)}>
            {(settings?.availableProviders ?? ['pasha']).map(code => (
              <option key={code} value={code}>{providerLabel(code, t)}</option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('currency')}</label>
          <select value={currency} disabled={!canSave} onChange={event => setCurrency(event.target.value)}>
            {['AZN', 'USD', 'EUR'].map(code => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('language')}</label>
          <select value={language} disabled={!canSave} onChange={event => setLanguage(event.target.value)}>
            <option value="az">az</option>
            <option value="en">en</option>
            <option value="ru">ru</option>
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('environment')}</label>
          <select value={environment} disabled={!canSave} onChange={event => setEnvironment(event.target.value)}>
            <option value="live">{t('environmentLive')}</option>
            <option value="test">{t('environmentTest')}</option>
          </select>
        </div>
        <div className={`${styles.field} ${styles.fieldWide}`}>
          <label className={styles.label}>{t('hasCredentials')}</label>
          <p className={settings?.hasCredentials ? styles.credentialsOk : styles.credentialsMissing}>
            {settings?.hasCredentials ? t('credentialsConfigured') : t('credentialsMissing')}
          </p>
          <p className={styles.hint}>{t('credentialsHint')}</p>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{settings?.hasCredentials ? t('replaceCertificate') : t('pkcs12File')}</label>
          <input
            type="file"
            accept=".p12,.pfx,application/x-pkcs12"
            disabled={!canSave}
            onChange={event => setPkcs12File(event.target.files?.[0] ?? null)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('pkcs12Password')}</label>
          <input
            type="password"
            autoComplete="new-password"
            value={pkcs12Password}
            disabled={!canSave}
            onChange={event => setPkcs12Password(event.target.value)}
          />
        </div>
        <div className={`${styles.field} ${styles.fieldWide}`}>
          <label className={styles.label}>{t('returnPath')}</label>
          <input value={returnPath} disabled={!canSave} onChange={event => setReturnPath(event.target.value)} />
          <p className={styles.hint}>{t('returnPathHint')}</p>
        </div>
      </div>

      <button className={styles.advancedToggle} type="button" onClick={() => setAdvancedOpen(current => !current)}>
        {advancedOpen ? t('hideAdvanced') : t('advanced')}
      </button>
      {advancedOpen ? (
        <div className={styles.formGrid}>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <label className={styles.label}>{t('merchantHandlerUrl')}</label>
            <input value={merchantHandlerUrl} disabled={!canSave} onChange={event => setMerchantHandlerUrl(event.target.value)} />
          </div>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <label className={styles.label}>{t('clientHandlerUrl')}</label>
            <input value={clientHandlerUrl} disabled={!canSave} onChange={event => setClientHandlerUrl(event.target.value)} />
          </div>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <label className={styles.label}>{t('caCertificatePem')}</label>
            <textarea value={caCertificatePem} disabled={!canSave} onChange={event => setCaCertificatePem(event.target.value)} />
          </div>
        </div>
      ) : null}

      {canSave ? (
        <div className={styles.formActions}>
          <button className="btn btn-primary" type="submit" disabled={saving}>{t('save')}</button>
        </div>
      ) : (
        <p className={styles.hint}>{t('noPermission')}</p>
      )}
    </form>
  );
};
